import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { cookieUtils } from "../utils/cookieUtils";

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useToggle(
  defaultValue?: boolean
): [boolean, () => void, Dispatch<SetStateAction<boolean>>] {
  const [value, setValue] = useState(!!defaultValue);

  const toggle = useCallback(() => setValue((x) => !x), []);

  return [value, toggle, setValue];
}

export function useLocalStorageToggle(
  key: string,
  defaultValue = false
): [boolean, () => void, (x: boolean) => void] {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  });

  const toggle = useCallback(() => {
    setValue((prevValue: any) => {
      const newValue = !prevValue;
      try {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
      return newValue;
    });
  }, [key]);

  const _setValue = useCallback(
    (newVal: boolean) => {
      setValue(newVal);
      try {
        window.localStorage.setItem(key, JSON.stringify(newVal));
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
    },
    [key]
  );

  return [value, toggle, _setValue];
}

export function useSecureCookieToggle(
  key: string
): [boolean, () => void, (x: boolean) => void] {
  const [value, setValue] = useState(() => {
    const cookieValue = cookieUtils.getSecureCookie(key);
    return cookieValue === true;
  });

  const toggle = useCallback(() => {
    setValue((prevValue) => {
      const newValue = !prevValue;
      cookieUtils.setSecureCookie(key, newValue, { expires: 7 });
      return newValue;
    });
  }, [key]);

  const _setValue = useCallback(
    (newVal: boolean) => {
      setValue(newVal);
      cookieUtils.setSecureCookie(key, newVal, { expires: 7 });
    },
    [key]
  );

  return [value, toggle, _setValue];
}

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

export const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const IS_SERVER = typeof window === "undefined";

export function useMediaQuery(
  query: string,
  {
    defaultValue = false,
    initializeWithValue = true,
  }: UseMediaQueryOptions = {}
): boolean {
  const getMatches = (query: string): boolean => {
    if (IS_SERVER) {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query);
    }
    return defaultValue;
  });

  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    handleChange();

    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);

  return matches;
}
